import { create } from 'zustand';

import type { LoginResponse } from '@/api/types/auth';
import { hasDeviceSession } from '@/config/auth/deviceToken';
import { getUserEmail } from '@/config/office/office';
import { queryClient } from '@/config/query/queryClient';
import { queryKeys } from '@/config/query/queryKeys';

interface AccountMismatch {
  /** The mailbox connected to the signed-in Lucio account (server truth). */
  connectedMailbox: string;
  /** The Outlook host mailbox the user is actually viewing. */
  hostMailbox: string;
}

interface AuthState {
  accountMismatch: AccountMismatch | null;
  authError: string | null;
  checkAccountMismatch: () => void;
  dismissCurrentMismatch: () => void;
  /** Generic auth-adjacent busy flag (e.g. session-by-context resolution). */
  isLoading: boolean;
  setAccountMismatch: (mismatch: AccountMismatch | null) => void;
  setAuthError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
}

// Dismissed-mismatch fingerprint persists across taskpane reloads — Outlook
// recycles the WebView frequently and we don't want the user to see the same
// false positive every two hours. The fingerprint includes both sides of the
// comparison so a *different* mismatch (e.g. the user actually signed in as
// the wrong person later) still surfaces.
const DISMISSED_MISMATCH_KEY = 'auth.dismissedMismatch';

const readDismissedMismatch = (): AccountMismatch | null => {
  try {
    const raw = localStorage.getItem(DISMISSED_MISMATCH_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<AccountMismatch>;
    if (typeof parsed?.connectedMailbox !== 'string' || typeof parsed?.hostMailbox !== 'string') {
      return null;
    }
    return { connectedMailbox: parsed.connectedMailbox, hostMailbox: parsed.hostMailbox };
  } catch {
    return null;
  }
};

const writeDismissedMismatch = (mismatch: AccountMismatch | null): void => {
  // Some embedded WebViews (notably certain Outlook desktop builds and
  // policy-locked tenants) can throw on localStorage writes — quota,
  // disabled storage, or privacy modes. Persistence is a UX optimization
  // here, not load-bearing, so fail open and let the in-memory state stand.
  try {
    if (mismatch) {
      localStorage.setItem(DISMISSED_MISMATCH_KEY, JSON.stringify(mismatch));
    } else {
      localStorage.removeItem(DISMISSED_MISMATCH_KEY);
    }
  } catch {
    // intentional: persistence is best-effort
  }
};

// Exported so sign-out flows can clear the fingerprint.
export const clearDismissedMismatch = (): void => writeDismissedMismatch(null);

const sameMismatch = (a: AccountMismatch | null, b: AccountMismatch | null): boolean =>
  !!a &&
  !!b &&
  a.connectedMailbox.toLowerCase() === b.connectedMailbox.toLowerCase() &&
  a.hostMailbox.toLowerCase() === b.hostMailbox.toLowerCase();

export const useAuthStore = create<AuthState>()((set, get) => ({
  accountMismatch: null,
  authError: null,
  /**
   * Mismatch = the Outlook host mailbox is not one of the connected mailbox's
   * addresses. The backend's alias list (UPN, mail, proxyAddresses) is the
   * source of truth for which addresses belong to the connected user — MSAL
   * cache state no longer participates (the mailbox connection, not the MSAL
   * account, is the identity that matters).
   */
  checkAccountMismatch: () => {
    try {
      const outlookEmail = getUserEmail();

      // Office.js not ready yet — wait for next tick. Don't clear or set mismatch
      // here, since acting on incomplete info can either show a false positive or
      // silently pass over a real one.
      if (!outlookEmail) {
        return;
      }
      const outlookEmailLower = outlookEmail.toLowerCase();

      const sessionUser = queryClient.getQueryData<LoginResponse['user']>(queryKeys.auth.user());

      // No connected user yet (pre-connect, or /auth/me not loaded) — nothing
      // to compare against. Real mismatches surface once the profile loads.
      if (!sessionUser?.email) {
        set({ accountMismatch: null });
        return;
      }

      const knownAddresses = [sessionUser.email, ...(sessionUser.aliases ?? [])].map((a) =>
        a.toLowerCase(),
      );
      if (knownAddresses.includes(outlookEmailLower)) {
        set({ accountMismatch: null });
        return;
      }

      const candidate: AccountMismatch = {
        connectedMailbox: sessionUser.email,
        hostMailbox: outlookEmail,
      };
      // Respect the user's "continue anyway" choice from a prior taskpane mount,
      // unless the underlying mismatch is now different.
      if (sameMismatch(candidate, readDismissedMismatch())) {
        set({ accountMismatch: null });
        return;
      }
      console.warn('Account mismatch detected', sessionUser.email, outlookEmail);
      set({ accountMismatch: candidate });
    } catch (error) {
      console.error('Failed to check account mismatch:', error);
      set({ accountMismatch: null });
    }
  },
  dismissCurrentMismatch: () => {
    const current = get().accountMismatch;
    if (!current) {
      return;
    }
    writeDismissedMismatch(current);
    set({ accountMismatch: null });
  },
  isLoading: false,
  setAccountMismatch: (mismatch: AccountMismatch | null) => set({ accountMismatch: mismatch }),
  setAuthError: (error: string | null) => set({ authError: error }),
  setLoading: (loading: boolean) => set({ isLoading: loading }),
}));

/**
 * Signed-in = a device session is stored. Evaluated on render — components
 * re-render on navigation after login/logout, which is when this changes.
 */
export const useIsAuthenticated = () => {
  return { isAuthenticated: hasDeviceSession(), isLoading: false };
};
